/**
 * ChipCancellationResolveDialog - Etapa 3: ação única para o lote/protocolo inteiro.
 *
 * Confirmar (chips passam a Cancelado), Recusar ou Desistir, com lista opcional de
 * exceções (colar/importar ICCIDs) que recebe uma ação própria. Antes de gravar mostra
 * o resumo e pede confirmação. Processamento atômico no servidor.
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useChipCancelMutations } from '@/hooks/useStockChipCancellations';
import { ChipValuesInput } from './ChipValuesInput';

export type ResolveAction = 'confirm' | 'reject' | 'withdraw';

const TITLE: Record<ResolveAction, string> = {
  confirm: 'Confirmar cancelamento',
  reject: 'Recusar',
  withdraw: 'Desistir do cancelamento',
};

const today = () => new Date().toLocaleDateString('en-CA');

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  targetType: 'batch' | 'protocol';
  targetId: string;
  label: string;
  /** Chips pendentes que recebem a ação */
  pending: number;
  action: ResolveAction;
}

export function ChipCancellationResolveDialog({ open, onOpenChange, targetType, targetId, label, pending, action }: Props) {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(today());
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [excAction, setExcAction] = useState<'reject' | 'keep'>('keep');
  const [confirming, setConfirming] = useState(false);
  const { resolve } = useChipCancelMutations();

  useEffect(() => { if (open) { setReason(''); setDate(today()); setExceptions([]); setExcAction('keep'); setConfirming(false); } }, [open]);

  const needsReason = action !== 'confirm';
  const main = Math.max(pending - exceptions.length, 0);
  const verb = action === 'confirm' ? 'serão cancelados' : action === 'reject' ? 'serão recusados' : 'sairão do cancelamento';

  const submit = () => resolve.mutate(
    { targetType, targetId, action, reason, cancelledAt: action === 'confirm' ? date : null, exceptions, exceptionAction: exceptions.length ? excAction : null },
    { onSuccess: () => onOpenChange(false), onSettled: () => setConfirming(false) },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{TITLE[action]}</DialogTitle>
          <DialogDescription>{label} · {pending.toLocaleString('pt-BR')} chip(s) pendente(s). A ação vale para todos.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {action === 'confirm' && (
            <div className="space-y-1.5">
              <Label>Data do cancelamento no broker</Label>
              <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Motivo / observação{needsReason ? ' *' : ''}</Label>
            <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </div>

          {action !== 'withdraw' && (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">Exceções (opcional)</p>
              <p className="text-xs text-muted-foreground">Quando o broker não atendeu todos, cole ou importe os ICCIDs que ficaram de fora.</p>
              {exceptions.length > 0 ? (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span><strong>{exceptions.length.toLocaleString('pt-BR')}</strong> exceção(ões) informada(s)</span>
                  <Button size="sm" variant="ghost" onClick={() => setExceptions([])}>Limpar</Button>
                </div>
              ) : (
                <ChipValuesInput onSubmit={(v) => setExceptions(v)} submitLabel="Usar como exceções" />
              )}
              {exceptions.length > 0 && (
                <RadioGroup value={excAction} onValueChange={(v) => setExcAction(v as 'reject' | 'keep')} className="gap-2">
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="keep" />Deixar pendentes (aguardando o broker)</label>
                  <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="reject" />Marcar como recusados</label>
                </RadioGroup>
              )}
            </div>
          )}

          {confirming && (
            <InlineNotice variant="warning">
              {main.toLocaleString('pt-BR')} chip(s) {verb}{exceptions.length ? ` e ${exceptions.length.toLocaleString('pt-BR')} exceção(ões) ficarão ${excAction === 'keep' ? 'pendentes' : 'recusadas'}` : ''}. Esta ação não pode ser desfeita. Confirmar?
            </InlineNotice>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => (confirming ? setConfirming(false) : onOpenChange(false))}>{confirming ? 'Voltar' : 'Cancelar'}</Button>
          <Button variant={action === 'confirm' ? 'default' : 'destructive'} disabled={(needsReason && !reason.trim()) || resolve.isPending}
            onClick={() => (confirming ? submit() : setConfirming(true))}>
            {resolve.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {confirming ? 'Sim, aplicar a todos' : TITLE[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
